import type { Session } from '@supabase/supabase-js';

const configuredBase =
  (import.meta.env.VITE_STUDIKA_API_BASE as string | undefined) ?? 'https://api.studikabahasa.com';

const apiBase = configuredBase.replace(/\/+$/, '');

export interface CreditPack {
  id: string;
  credits: number;
  price_idr: number;
  price_per_credit_idr: number;
  popular: boolean;
}

export interface TopupResponse {
  snap_token: string;
  redirect_url: string;
  order_id: string;
  pack: {
    id: string;
    credits: number;
    price_idr: number;
  };
}

export interface GroupMemberView {
  id?: string;
  memberId?: string;
  userId: string | null;
  name: string | null;
  role: 'leader' | 'member';
  paymentStatus: 'unpaid' | 'paid';
}

export interface GroupBundleView {
  id: string;
  status: 'forming' | 'active' | 'cancelled';
  sizeK: number;
  pricePerPersonIdr: number;
  members: GroupMemberView[];
  inviteCode: string | null;
}

export interface PayGroupBundleResponse {
  orderId: string;
  snapToken: string;
  snapRedirectUrl: string;
  amountIdr: number;
  status: 'pending';
}

interface ApiErrorBody {
  message?: string;
  error?: string;
  code?: string;
}

function apiPath(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (apiBase.endsWith('/api/v1') || normalized.startsWith('/api/v1/')) {
    return `${apiBase}${normalized}`;
  }
  return `${apiBase}/api/v1${normalized}`;
}

function idempotencyKey(prefix: string): string {
  if ('crypto' in window && typeof window.crypto.randomUUID === 'function') {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    return body.message ?? body.error ?? body.code ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

async function apiFetch<T>(
  path: string,
  session: Session | null,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (session?.access_token) {
    headers.set('authorization', `Bearer ${session.access_token}`);
  }

  const res = await fetch(apiPath(path), {
    ...init,
    headers,
  });

  if (!res.ok) {
    throw new Error(await parseError(res));
  }

  return (await res.json()) as T;
}

export async function getCreditPacks(): Promise<CreditPack[]> {
  const data = await apiFetch<{ packs: CreditPack[] }>('/wallet/packs', null);
  return data.packs;
}

export function startCreditTopup(
  session: Session,
  studentId: string,
  packId: string,
): Promise<TopupResponse> {
  return apiFetch<TopupResponse>('/wallet/topup', session, {
    method: 'POST',
    headers: {
      'idempotency-key': idempotencyKey('web-credit-pack'),
    },
    body: JSON.stringify({
      student_id: studentId,
      pack_id: packId,
    }),
  });
}

export function joinGroupByInvite(session: Session, code: string): Promise<GroupBundleView> {
  return apiFetch<GroupBundleView>(`/group-bundles/join/${encodeURIComponent(code)}`, session, {
    method: 'POST',
  });
}

export function getGroupBundle(session: Session, groupId: string): Promise<GroupBundleView> {
  return apiFetch<GroupBundleView>(`/group-bundles/${encodeURIComponent(groupId)}`, session);
}

export function giveGroupConsent(
  session: Session,
  groupId: string,
  memberId: string,
  termsVersion: string,
): Promise<{ ok?: boolean; status?: string }> {
  return apiFetch<{ ok?: boolean; status?: string }>(
    `/group-bundles/${encodeURIComponent(groupId)}/consent`,
    session,
    {
      method: 'POST',
      body: JSON.stringify({
        memberId,
        agree: true,
        termsVersion,
      }),
    },
  );
}

export function payGroupShare(session: Session, groupId: string): Promise<PayGroupBundleResponse> {
  return apiFetch<PayGroupBundleResponse>(`/group-bundles/${encodeURIComponent(groupId)}/pay`, session, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
